import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Building2, ClipboardList, Send, PackageCheck, Ban, FilePlus, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { getApiUrl } from "@/utils/api";
import useAutoSave from "@/hooks/useAutoSave";
import { AutoSaveStatus } from "@/components/ui/AutoSaveStatus";

const API = getApiUrl();
const EMPTY = { name: "", contact_name: "", email: "", phone: "", website: "", address: "", postcode: "", commission: 0, nationwide: false, notes: "", enabled: true };

const STATUS_STYLES = {
  draft: "bg-muted text-muted-foreground",
  sent: "bg-blue-100 text-blue-700",
  received: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

export default function SuppliersAdmin() {
  const [tab, setTab] = useState("suppliers"); // "suppliers" | "pos"
  const [rows, setRows] = useState([]);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  // Purchase-order editor
  const [poDialog, setPoDialog] = useState(false);
  const [poEditing, setPoEditing] = useState(null);
  const [poForm, setPoForm] = useState({ supplier_id: "", items: [], notes: "", currency: "GBP" });
  const [poSaving, setPoSaving] = useState(false);
  const [testingEmailId, setTestingEmailId] = useState(null);

  const load = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const [s, p] = await Promise.all([
        axios.get(`${API}/admin/suppliers`, { withCredentials: true }),
        axios.get(`${API}/admin/purchase-orders`, { withCredentials: true }).catch(() => ({ data: { purchase_orders: [] } })),
      ]);
      setRows(s.data.suppliers || []);
      setPos(p.data.purchase_orders || []);
    } catch { toast.error("Failed to load"); }
    finally { if (!silent) setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY }); setShow(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name || "", contact_name: s.contact_name || "", email: s.email || "", phone: s.phone || "", website: s.website || "", address: s.address || "", postcode: s.postcode || "", commission: s.commission ?? 0, nationwide: s.nationwide === true, notes: s.notes || "", enabled: s.enabled !== false });
    setShow(true);
  };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); throw new Error("name required"); }
    try {
      setSaving(true);
      const body = { ...form, commission: Number(form.commission || 0) };
      if (editing) {
        await axios.put(`${API}/admin/suppliers/${editing.supplier_id}`, body, { withCredentials: true });
      } else {
        const { data } = await axios.post(`${API}/admin/suppliers`, body, { withCredentials: true });
        // Transition from create → edit so further autosaves target the
        // newly-minted supplier_id instead of spawning duplicates.
        setEditing(data);
      }
      // Silent refresh — never flash the loading spinner behind the dialog.
      load({ silent: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      throw e;
    } finally { setSaving(false); }
  };

  const autosave = useAutoSave(form, save, {
    delay: 1200,
    enabled: show,
    canSave: (v) => Boolean(v?.name?.trim()),
  });
  const remove = async (s) => {
    if (!confirm(`Delete supplier "${s.name}"?`)) return;
    try { await axios.delete(`${API}/admin/suppliers/${s.supplier_id}`, { withCredentials: true }); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const sendTestEmail = async (s) => {
    if (!s.email) { toast.error(`${s.name} has no email address on file`); return; }
    setTestingEmailId(s.supplier_id);
    try {
      await axios.post(`${API}/admin/suppliers/${s.supplier_id}/test-email`, {}, { withCredentials: true });
      toast.success(`Test email sent to ${s.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test email failed");
    } finally {
      setTestingEmailId(null);
    }
  };

  // ------ Purchase Orders ------
  const oneClickPO = async (s) => {
    try {
      const { data } = await axios.post(`${API}/admin/purchase-orders/from-supplier/${s.supplier_id}`, {}, { withCredentials: true });
      toast.success(`Draft ${data.po_number} created for ${s.name}`);
      setPoEditing(data);
      setPoForm({ supplier_id: data.supplier_id, items: data.items || [], notes: data.notes || "", currency: data.currency || "GBP" });
      setPoDialog(true);
      setTab("pos");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create PO"); }
  };

  const openBlankPO = () => {
    if (!rows.length) { toast.error("Add a supplier first"); return; }
    setPoEditing(null);
    // Force explicit supplier choice so the required-supplier validation
    // actually fires if the admin skips the dropdown.
    setPoForm({ supplier_id: "", items: [], notes: "", currency: "GBP" });
    setPoDialog(true);
  };

  const openEditPO = (po) => {
    setPoEditing(po);
    setPoForm({
      supplier_id: po.supplier_id,
      items: (po.items || []).map((it) => ({ ...it })),
      notes: po.notes || "",
      currency: po.currency || "GBP",
    });
    setPoDialog(true);
  };

  const savePO = async () => {
    if (!poForm.supplier_id) { toast.error("Choose a supplier"); return; }
    try {
      setPoSaving(true);
      const body = {
        supplier_id: poForm.supplier_id,
        items: (poForm.items || []).map((it) => ({
          product_id: it.product_id || "",
          name: it.name || "",
          quantity: Number(it.quantity) || 1,
          unit_cost: Number(it.unit_cost) || 0,
        })),
        notes: poForm.notes || "",
        currency: poForm.currency || "GBP",
      };
      if (poEditing) await axios.put(`${API}/admin/purchase-orders/${poEditing.po_id}`, body, { withCredentials: true });
      else await axios.post(`${API}/admin/purchase-orders`, body, { withCredentials: true });
      toast.success(poEditing ? "PO updated" : "PO created");
      setPoDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setPoSaving(false); }
  };

  const changePOStatus = async (po, status) => {
    try {
      await axios.post(`${API}/admin/purchase-orders/${po.po_id}/status`, { status }, { withCredentials: true });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const deletePO = async (po) => {
    if (!confirm(`Delete ${po.po_number}?`)) return;
    try { await axios.delete(`${API}/admin/purchase-orders/${po.po_id}`, { withCredentials: true }); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const setItem = (idx, patch) => {
    setPoForm((prev) => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], ...patch };
      return { ...prev, items };
    });
  };
  const addBlankItem = () => setPoForm((prev) => ({ ...prev, items: [...prev.items, { product_id: "", name: "", quantity: 1, unit_cost: 0 }] }));
  const removeItem = (idx) => setPoForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));

  const poSubtotal = (poForm.items || []).reduce((s, it) => s + Number(it.unit_cost || 0) * Number(it.quantity || 0), 0);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="suppliers-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><Building2 className="h-6 w-6" /> Suppliers</h2>
          <p className="text-sm text-muted-foreground mt-1">Companies you buy stock from, plus every purchase order raised against them.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap max-w-full">
          <div className="inline-flex border border-border rounded overflow-x-auto text-sm max-w-full" role="tablist">
            <button type="button" onClick={() => setTab("suppliers")}
              className={`px-3 py-1.5 font-medium transition-colors ${tab === "suppliers" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              data-testid="suppliers-subtab-suppliers">Suppliers ({rows.length})</button>
            <button type="button" onClick={() => setTab("pos")}
              className={`px-3 py-1.5 font-medium transition-colors ${tab === "pos" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              data-testid="suppliers-subtab-pos">Purchase Orders ({pos.length})</button>
          </div>
          {tab === "suppliers" && <Button onClick={openCreate} data-testid="suppliers-add-btn"><Plus className="h-4 w-4 mr-1" />Add Supplier</Button>}
          {tab === "pos" && <Button onClick={openBlankPO} data-testid="po-add-btn"><FilePlus className="h-4 w-4 mr-1" />New Purchase Order</Button>}
        </div>
      </div>

      {tab === "suppliers" && (
        <div className="border border-border rounded overflow-x-auto bg-background">
          {rows.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">No suppliers yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left">
                <tr><th className="p-3">Name</th><th className="p-3">Contact</th><th className="p-3">Email</th><th className="p-3">Phone</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr
                    key={s.supplier_id}
                    className="border-t cursor-pointer hover:bg-muted/40 transition-colors"
                    onClick={() => openEdit(s)}
                    data-testid={`supplier-row-${s.supplier_id}`}
                  >
                    <td className="p-3 font-medium">{s.name}{s.website && <a href={s.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="ml-2 text-xs text-primary underline">↗</a>}</td>
                    <td className="p-3">{s.contact_name || "—"}</td>
                    <td className="p-3">{s.email || "—"}</td>
                    <td className="p-3">{s.phone || "—"}</td>
                    <td className="p-3">{s.enabled === false
                      ? <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">Disabled</span>
                      : <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">Active</span>}</td>
                    <td className="p-3 text-right space-x-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <Button variant="outline" size="sm" onClick={() => oneClickPO(s)} data-testid={`supplier-newpo-${s.supplier_id}`} title="One-click purchase order"><ClipboardList className="h-3 w-3 mr-1" />New PO</Button>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => sendTestEmail(s)}
                        disabled={testingEmailId === s.supplier_id}
                        data-testid={`supplier-test-email-${s.supplier_id}`}
                        title="Send test email"
                      >
                        {testingEmailId === s.supplier_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`supplier-edit-${s.supplier_id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(s)} data-testid={`supplier-delete-${s.supplier_id}`} className="text-red-600 hover:bg-red-50"><Trash2 className="h-3 w-3" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "pos" && (
        <div className="border border-border rounded overflow-x-auto bg-background" data-testid="po-list">
          {pos.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              No purchase orders yet. Click <strong>New Purchase Order</strong>, or use the <strong>New PO</strong> shortcut on any supplier row to auto-fill their catalog.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="p-3">PO #</th>
                  <th className="p-3">Supplier</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Items</th>
                  <th className="p-3 text-right">Subtotal</th>
                  <th className="p-3">Created</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pos.map((po) => {
                  const editable = po.status !== "received" && po.status !== "cancelled";
                  return (
                    <tr key={po.po_id} className="border-t align-top" data-testid={`po-row-${po.po_id}`}>
                      <td className="p-3 font-mono text-xs">{po.po_number}</td>
                      <td className="p-3 font-medium">{po.supplier_name}</td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded capitalize ${STATUS_STYLES[po.status] || "bg-muted"}`} data-testid={`po-status-${po.po_id}`}>{po.status}</span>
                      </td>
                      <td className="p-3 text-muted-foreground">{po.items?.length || 0}</td>
                      <td className="p-3 text-right whitespace-nowrap">{po.currency || "GBP"} {Number(po.subtotal || 0).toFixed(2)}</td>
                      <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{po.created_at?.slice(0, 10)}</td>
                      <td className="p-3 text-right space-x-1 whitespace-nowrap">
                        {po.status === "draft" && (
                          <Button variant="outline" size="sm" onClick={() => changePOStatus(po, "sent")} data-testid={`po-send-${po.po_id}`} title="Mark as sent"><Send className="h-3 w-3" /></Button>
                        )}
                        {po.status === "sent" && (
                          <Button variant="outline" size="sm" onClick={() => changePOStatus(po, "received")} data-testid={`po-receive-${po.po_id}`} title="Mark as received" className="text-green-700"><PackageCheck className="h-3 w-3" /></Button>
                        )}
                        {editable && (
                          <Button variant="ghost" size="sm" onClick={() => changePOStatus(po, "cancelled")} data-testid={`po-cancel-${po.po_id}`} title="Cancel PO" className="text-red-600"><Ban className="h-3 w-3" /></Button>
                        )}
                        {editable && (
                          <Button variant="ghost" size="sm" onClick={() => openEditPO(po)} data-testid={`po-edit-${po.po_id}`}><Pencil className="h-3 w-3" /></Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => deletePO(po)} data-testid={`po-delete-${po.po_id}`} className="text-red-600 hover:bg-red-50"><Trash2 className="h-3 w-3" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Supplier dialog */}
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="supplier-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-4">
              <span>{editing ? "Edit Supplier" : "Add Supplier"}</span>
              <AutoSaveStatus status={autosave.status} />
            </DialogTitle>
            <DialogDescription>Full company profile — used in product links and purchase orders. Changes save automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Company name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Contact person</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="supplier-contact" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="supplier-email" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="supplier-phone" /></div>
              <div><Label>Website</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" data-testid="supplier-website" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-3">
              <div><Label>Address</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="supplier-address" /></div>
              <div>
                <Label>Postcode</Label>
                <Input
                  value={form.postcode}
                  onChange={(e) => setForm({ ...form, postcode: e.target.value.toUpperCase() })}
                  placeholder="SW1A 1AA"
                  className="uppercase"
                  data-testid="supplier-postcode"
                />
                <div className="flex items-center gap-2 mt-3">
                  <Switch
                    checked={form.nationwide === true}
                    onCheckedChange={(v) => setForm({ ...form, nationwide: v })}
                    data-testid="supplier-nationwide"
                  />
                  <Label className="mb-0">Nationwide</Label>
                </div>
                <Label className="mt-3 block">Commission (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={form.commission}
                  onChange={(e) => setForm({ ...form, commission: e.target.value })}
                  placeholder="0"
                  data-testid="supplier-commission"
                />
              </div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="supplier-notes" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="supplier-enabled" /><Label className="mb-0">Active</Label></div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={async () => { await autosave.saveNow(); setShow(false); load(); }}
              data-testid="supplier-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchase Order dialog */}
      <Dialog open={poDialog} onOpenChange={setPoDialog}>
        <DialogContent className="w-[95vw] sm:max-w-2xl" data-testid="po-dialog">
          <DialogHeader>
            <DialogTitle>{poEditing ? `Edit ${poEditing.po_number}` : "New Purchase Order"}</DialogTitle>
            <DialogDescription>Draft the order, then hit Save. Use the row actions to send, receive or cancel.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Supplier *</Label>
                <select className="w-full h-10 px-3 border border-input rounded bg-background" value={poForm.supplier_id} onChange={(e) => setPoForm({ ...poForm, supplier_id: e.target.value })} data-testid="po-supplier">
                  <option value="">Select…</option>
                  {rows.filter((s) => s.enabled !== false).map((s) => (
                    <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Currency</Label>
                <select className="w-full h-10 px-3 border border-input rounded bg-background" value={poForm.currency} onChange={(e) => setPoForm({ ...poForm, currency: e.target.value })}>
                  {["GBP", "USD", "EUR"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="mb-0">Line items</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addBlankItem} data-testid="po-add-line"><Plus className="h-3 w-3 mr-1" />Add line</Button>
              </div>
              {poForm.items.length === 0 ? (
                <div className="text-xs text-muted-foreground p-4 border border-dashed border-border rounded text-center">No lines yet. Click <strong>Add line</strong>, or one-click a PO from a supplier row to prefill.</div>
              ) : (
                <div className="border border-border rounded overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-left text-xs">
                      <tr><th className="p-2">Item</th><th className="p-2 w-20">Qty</th><th className="p-2 w-28">Unit cost</th><th className="p-2 w-28 text-right">Subtotal</th><th className="p-2 w-8"></th></tr>
                    </thead>
                    <tbody>
                      {poForm.items.map((it, idx) => {
                        const sub = Number(it.unit_cost || 0) * Number(it.quantity || 0);
                        return (
                          <tr key={idx} className="border-t border-border" data-testid={`po-line-${idx}`}>
                            <td className="p-2"><Input value={it.name} onChange={(e) => setItem(idx, { name: e.target.value })} placeholder="Product name" data-testid={`po-line-name-${idx}`} /></td>
                            <td className="p-2"><Input type="number" min="1" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} data-testid={`po-line-qty-${idx}`} /></td>
                            <td className="p-2"><Input type="number" step="0.01" min="0" value={it.unit_cost} onChange={(e) => setItem(idx, { unit_cost: e.target.value })} data-testid={`po-line-cost-${idx}`} /></td>
                            <td className="p-2 text-right whitespace-nowrap">{poForm.currency} {sub.toFixed(2)}</td>
                            <td className="p-2"><Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="text-red-600" data-testid={`po-line-remove-${idx}`}><Trash2 className="h-3 w-3" /></Button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-border bg-muted/20">
                        <td className="p-2 text-right font-medium" colSpan={3}>Subtotal</td>
                        <td className="p-2 text-right font-semibold whitespace-nowrap" data-testid="po-subtotal">{poForm.currency} {poSubtotal.toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={poForm.notes} onChange={(e) => setPoForm({ ...poForm, notes: e.target.value })} placeholder="Delivery address, PO reference, etc." data-testid="po-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPoDialog(false)}>Cancel</Button>
            <Button onClick={savePO} disabled={poSaving} data-testid="po-save">{poSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : (poEditing ? "Update" : "Create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
